import { useMemo } from "react"
import { differenceInMinutes, format, isSameYear, isToday, isYesterday, parseISO } from "date-fns"
import { Broadcast } from "phosphor-react"
import pluralize from "pluralize"
import { LiveSessionStatus, ShareAttachmentFragment } from "~/__generated__/graphql"
import { AppText } from "~/ui/app-text"
import { UserAvatar } from "~/ui/user-avatar"

type LiveSessionData = NonNullable<ShareAttachmentFragment["liveSession"]>

interface Props {
  liveSession: LiveSessionData
}

export const MessageAttachmentLiveSession: React.FC<Props> = ({ liveSession }) => {
  const isLive = liveSession.status === LiveSessionStatus.Started
  const isEnded = liveSession.status === LiveSessionStatus.Ended

  const startedAtText = useMemo(() => {
    if (!liveSession.startedAt) return null
    const date = parseISO(liveSession.startedAt)
    const time = format(date, "h:mmaaa")
    if (isToday(date)) return `Today at ${time}`
    if (isYesterday(date)) return `Yesterday at ${time}`
    if (isSameYear(date, new Date())) return format(date, `MMM d 'at' h:mmaaa`)
    return format(date, `MMM d, yyyy 'at' h:mmaaa`)
  }, [liveSession.startedAt])

  const durationText = useMemo(() => {
    if (!isEnded || !liveSession.startedAt || !liveSession.endedAt) return null
    const totalMinutes = differenceInMinutes(
      parseISO(liveSession.endedAt),
      parseISO(liveSession.startedAt)
    )
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    if (hours > 0) return `${hours}h ${minutes}m`
    return `${minutes}m`
  }, [isEnded, liveSession.startedAt, liveSession.endedAt])

  const defaultTitle = useMemo(() => {
    const preview = liveSession.participantsOnstagePreview ?? []
    const count = liveSession.participantsOnstageCount ?? 0
    if (count === 1 && preview[0]) return `Live with ${preview[0].displayName}`
    if (count === 2 && preview[0] && preview[1])
      return `Live with ${preview[0].displayName} and ${preview[1].displayName}`
    return `Live with ${pluralize("host", count, true)}`
  }, [liveSession.participantsOnstagePreview, liveSession.participantsOnstageCount])

  const thumbnailUrl = isLive || isEnded ? liveSession.thumbnailUrl : null

  const participants = liveSession.participantsOnstagePreview ?? []

  return (
    <div className="flex w-full min-w-[260px] max-w-[360px] flex-col overflow-hidden rounded-xl border border-neutral-200 bg-neutral-100">
      {/* Header */}
      <div className="flex flex-col gap-2 p-4">
        {/* "Live Space" label + status */}
        <div className="flex flex-row items-center justify-between">
          <AppText variant="hairline-2">Live Space</AppText>
          <div className="flex flex-row items-center gap-1">
            {isLive && (
              <AppText variant="hairline-2" className="text-support-green">
                Live Now
              </AppText>
            )}
            {isEnded && (
              <AppText variant="hairline-2" className="text-base-gray">
                Ended
              </AppText>
            )}
            <Broadcast
              size={16}
              weight="bold"
              className={isLive ? "text-support-green" : "text-base-gray"}
            />
          </div>
        </div>

        {/* Title */}
        <AppText variant="h3" className="line-clamp-2">
          {liveSession.title || defaultTitle}
        </AppText>

        {/* Start time + duration */}
        {startedAtText && (
          <AppText variant="hairline-2">
            {startedAtText}
            {durationText ? ` · ${durationText}` : ""}
          </AppText>
        )}

        {/* Hosts row + thumbnail */}
        <div className="flex flex-row items-start justify-between gap-2">
          <div className="flex flex-col gap-1">
            {participants.length > 0 && (
              <div className="flex flex-row items-center gap-1.5">
                {/* Overlapping avatars */}
                <div className="flex flex-row">
                  {participants.slice(0, 4).map((user, index) => (
                    <div
                      key={user.id}
                      className="relative"
                      style={{
                        marginLeft: index > 0 ? -6 : 0,
                        zIndex: participants.length - index,
                      }}
                    >
                      <UserAvatar
                        avatarUrl={user.avatarUrl}
                        displayName={user.displayName}
                        size="xsm"
                      />
                    </div>
                  ))}
                </div>
                <AppText variant="body3-medium" className="text-neutral-500">
                  {pluralize("host", liveSession.participantsOnstageCount, true)}
                </AppText>
              </div>
            )}
            {isLive && !!liveSession.viewerCount && (
              <AppText variant="body3-medium" className="text-neutral-500">
                {pluralize("viewer", liveSession.viewerCount, true)}
              </AppText>
            )}
            {isEnded && !!liveSession.viewCount && (
              <AppText variant="body3-medium" className="text-neutral-500">
                {pluralize("view", liveSession.viewCount, true)}
              </AppText>
            )}
          </div>

          {/* Replay thumbnail */}
          {thumbnailUrl && (
            <div className="relative flex-shrink-0 overflow-hidden rounded">
              <img
                src={thumbnailUrl}
                alt="Live space thumbnail"
                className="h-[60px] w-[90px] object-cover"
              />
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white/90">
                  <div className="ml-0.5 h-0 w-0 border-b-[5px] border-l-[9px] border-t-[5px] border-b-transparent border-l-black border-t-transparent" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
